<template>
  <article class="inventory-list-item" :class="{'inventory-list-item--selected': selected}">
    <div class="inventory-list-item__selector">
      <label v-if="canWrite && canSelect">
        <input
          type="checkbox"
          :checked="selected"
          :aria-label="`${$t('Inventory.select_vehicle')} ${orderId}`"
          @change="$emit('toggle-selection', orderId)"
        >
        <span class="sr-only">{{ $t('Inventory.select') }}</span>
      </label>
    </div>

    <div class="inventory-list-item__media">
      <img
        v-if="imageUrl && !imageFailed"
        :src="imageUrl"
        :alt="vehicleTitle"
        @error="imageFailed = true"
      >
      <div v-else :aria-label="$t('Inventory.no_photo')">
        <i class="fa fa-car" aria-hidden="true" />
      </div>
    </div>

    <div class="inventory-list-item__identity">
      <div class="inventory-list-item__identity-topline">
        <router-link :to="{name: 'OrderInfo', query: {orderId: orderId}}">
          {{ orderId }}
        </router-link>
        <span class="inventory-list-item__state" :class="stateClass">
          {{ hasLeft ? $t('Inventory.state_left') : $t('Inventory.state_inside') }}
        </span>
        <status-badge
          v-if="order.status"
          :status="order.status"
          size="sm"
        />
      </div>
      <strong>{{ vehicleTitle }}</strong>
      <small>{{ $t('Inventory.vin') }} · {{ order.vin || $t('Inventory.not_provided') }}</small>
    </div>

    <div class="inventory-list-item__account">
      <span>{{ $t('Inventory.billing_name') }}</span>
      <strong>{{ $realName(order, 'payer') || '—' }}</strong>
      <small>{{ sourceReference }}</small>
    </div>

    <div class="inventory-list-item__storage">
      <label
        v-if="canWrite"
        class="inventory-list-item__lot inventory-list-item__lot--editable"
        :class="{'is-unassigned': !inventoryLot}"
      >
        <i class="fa fa-map-marker" aria-hidden="true" />
        <span class="sr-only">{{ $t('Inventory.change_lot', {orderId: orderId}) }}</span>
        <select
          :value="inventoryLot"
          :disabled="updatingLot"
          :aria-label="$t('Inventory.change_lot', {orderId: orderId})"
          @change="changeLot"
        >
          <option value="" disabled>{{ $t('Inventory.choose_lot') }}</option>
          <option v-for="lot in inventoryLots" :key="lot" :value="lot">{{ $t('Inventory.lot') }} {{ lot }}</option>
        </select>
        <i v-if="updatingLot" class="fa fa-spinner fa-spin" aria-hidden="true" />
        <i v-else class="fa fa-chevron-down inventory-list-item__lot-chevron" aria-hidden="true" />
      </label>
      <span v-else class="inventory-list-item__lot" :class="{'is-unassigned': !inventoryLot}">
        <i class="fa fa-map-marker" aria-hidden="true" />
        <template v-if="inventoryLot">{{ $t('Inventory.lot') }} {{ inventoryLot }}</template>
        <template v-else>{{ $t('Inventory.unassigned_lot') }}</template>
      </span>
      <span class="inventory-list-item__term" :class="{'is-unassigned': !inventoryTerm}">
        <i class="fa fa-clock-o" aria-hidden="true" />
        {{ inventoryTermLabel }}
      </span>
    </div>

    <div class="inventory-list-item__dates">
      <div>
        <span>{{ $t('Inventory.date_entered') }}</span>
        <strong>{{ formattedDateEntered }}</strong>
      </div>
      <div>
        <span>{{ $t('Inventory.date_left') }}</span>
        <strong>{{ formattedDateLeft }}</strong>
      </div>
    </div>

    <div class="inventory-list-item__actions">
      <button
        v-if="canWrite && canConfirm"
        type="button"
        class="btn btn-kne-green"
        :disabled="confirming"
        @click="$emit('confirm-arrival', order)"
      >
        <i :class="['fa', confirming ? 'fa-spinner fa-spin' : 'fa-check']" aria-hidden="true" />
        {{ confirming ? $t('Inventory.confirming_arrival') : $t('Inventory.confirm_arrival') }}
      </button>
      <router-link
        class="btn btn-light"
        :to="{name: 'OrderInfo', query: {orderId: orderId}}"
      >
        {{ $t('Inventory.view_order') }}
      </router-link>
      <button
        v-if="canWrite && canBill"
        type="button"
        class="btn btn-kne-orange"
        @click="$emit('bill', order)"
      >
        <i class="fa fa-check-circle" aria-hidden="true" />
        {{ $t('Inventory.billed_done') }}
      </button>
    </div>
  </article>
</template>

<script>
import StatusBadge from '../Other-components/StatusBadge.vue'
import { formatTorontoDateTime } from './inventoryDateTime'
import {
  INVENTORY_TERM,
  canConfirmInventoryArrival,
  isInventoryTerm,
  normalizeInventoryLotName
} from '../../../../inventory/inventorySelectors'

export default {
  name: 'InventoryListItem',

  components: {
    StatusBadge
  },

  props: {
    order: {
      type: Object,
      required: true
    },
    imageUrl: {
      type: String,
      default: ''
    },
    selected: {
      type: Boolean,
      default: false
    },
    canWrite: {
      type: Boolean,
      default: false
    },
    confirming: {
      type: Boolean,
      default: false
    },
    updatingLot: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      imageFailed: false
    }
  },

  computed: {
    inventoryLots () {
      return this.$store.getters.inventoryLotNames
    },
    orderId () {
      return String(this.order['.key'] || this.order.orderNumber || this.order.key || '')
    },
    vehicleTitle () {
      const description = [this.order.year, this.order.make, this.order.model, this.order.trim]
        .filter(Boolean)
        .join(' ')
      return description || this.$t('Inventory.vehicle_pending')
    },
    hasLeft () {
      return Boolean(this.order.dateLeft)
    },
    canSelect () {
      return Boolean(this.order.dateEntered) && !this.order.dateLeft
    },
    canBill () {
      return Boolean(this.order.dateEntered) && Boolean(this.order.dateLeft) && !this.order.inventoryBilledAt
    },
    canConfirm () {
      return canConfirmInventoryArrival(this.order)
    },
    stateClass () {
      return this.hasLeft ? 'inventory-list-item__state--left' : 'inventory-list-item__state--inside'
    },
    inventoryLot () {
      const lot = normalizeInventoryLotName(this.order.inventoryLot)
      return this.inventoryLots.includes(lot) ? lot : ''
    },
    inventoryTerm () {
      const term = String(this.order.inventoryTerm || '').trim()
      return isInventoryTerm(term) ? term : ''
    },
    inventoryTermLabel () {
      if (this.inventoryTerm === INVENTORY_TERM.SHORT_TERM) return this.$t('Inventory.short_term')
      if (this.inventoryTerm === INVENTORY_TERM.LONG_TERM) return this.$t('Inventory.long_term')
      return this.$t('Inventory.unassigned_term')
    },
    sourceReference () {
      const source = this.order.source || this.$t('Inventory.title')
      const reference = this.order.numRef || this.order.sourceOrderId
      return reference ? `${source} · ${reference}` : source
    },
    formattedDateEntered () {
      return formatTorontoDateTime(this.order.dateEntered)
    },
    formattedDateLeft () {
      return formatTorontoDateTime(this.order.dateLeft)
    }
  },

  watch: {
    imageUrl () {
      this.imageFailed = false
    }
  },

  methods: {
    changeLot (event) {
      this.$emit('change-lot', {
        order: this.order,
        inventoryLot: event.target.value
      })
    }
  }
}
</script>

<style scoped>
.inventory-list-item {
  display: grid;
  grid-template-columns: 28px 76px minmax(190px, 1.35fr) minmax(140px, 1fr) minmax(130px, .8fr) minmax(160px, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #e3e8ec;
  border-radius: 13px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(27, 39, 51, .05);
  transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease;
}

.inventory-list-item:hover {
  border-color: #d6dde3;
  box-shadow: 0 8px 22px rgba(27, 39, 51, .09);
  transform: translateY(-1px);
}

.inventory-list-item--selected {
  border-color: #e7a65e;
  background: #fffaf4;
  box-shadow: 0 0 0 2px rgba(231, 166, 94, .13), 0 8px 22px rgba(27, 39, 51, .08);
}

.inventory-list-item__selector {
  display: flex;
  align-items: center;
  justify-content: center;
}

.inventory-list-item__selector label {
  display: flex;
  margin: 0;
  cursor: pointer;
}

.inventory-list-item__selector input {
  width: 17px;
  height: 17px;
}

.inventory-list-item__media {
  width: 76px;
  height: 58px;
  overflow: hidden;
  border-radius: 9px;
  background: #edf1f4;
}

.inventory-list-item__media img,
.inventory-list-item__media div {
  width: 100%;
  height: 100%;
}

.inventory-list-item__media img {
  object-fit: cover;
}

.inventory-list-item__media div {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8995a0;
  background: linear-gradient(135deg, #f4f6f7, #e4e9ed);
  font-size: 1.45rem;
}

.inventory-list-item__identity,
.inventory-list-item__account,
.inventory-list-item__dates {
  min-width: 0;
}

.inventory-list-item__identity {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.inventory-list-item__identity-topline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
}

.inventory-list-item__identity-topline > a {
  color: #d97918;
  font-size: .75rem;
  font-weight: 800;
  letter-spacing: .04em;
}

.inventory-list-item__identity > strong {
  overflow: hidden;
  color: #2f3d4a;
  font-size: .9rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inventory-list-item__identity > small {
  overflow: hidden;
  color: #7b8792;
  font-family: monospace;
  font-size: .7rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inventory-list-item__state {
  display: inline-flex;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: .62rem;
  font-weight: 750;
  white-space: nowrap;
}

.inventory-list-item__state--inside {
  color: #17663c;
  background: #ddf7e8;
}

.inventory-list-item__state--left {
  color: #794f0a;
  background: #fff0c8;
}

.inventory-list-item__account {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.inventory-list-item__account > span,
.inventory-list-item__dates span {
  color: #89939d;
  font-size: .64rem;
  font-weight: 700;
}

.inventory-list-item__account > strong {
  overflow: hidden;
  color: #3d4b58;
  font-size: .78rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inventory-list-item__account > small {
  overflow: hidden;
  color: #7f8a94;
  font-size: .66rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inventory-list-item__storage {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 5px;
}

.inventory-list-item__lot,
.inventory-list-item__term {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 4px 7px;
  border-radius: 7px;
  font-size: .67rem;
  font-weight: 750;
  white-space: nowrap;
}

.inventory-list-item__lot {
  color: #6f420f;
  background: #fff0df;
}

.inventory-list-item__lot i {
  color: #d97918;
}

.inventory-list-item__term {
  color: #28566e;
  background: #eaf5fa;
}

.inventory-list-item__term i {
  color: #3e718e;
}

.inventory-list-item__lot.is-unassigned,
.inventory-list-item__term.is-unassigned {
  color: #727e89;
  background: #f0f2f4;
  font-weight: 700;
}

.inventory-list-item__lot--editable {
  margin: 0;
  cursor: pointer;
}

.inventory-list-item__lot--editable select {
  max-width: 78px;
  padding: 0;
  border: 0;
  outline: 0;
  color: inherit;
  background: transparent;
  cursor: pointer;
  font: inherit;
  appearance: none;
}

.inventory-list-item__lot--editable select:disabled {
  cursor: wait;
  opacity: .7;
}

.inventory-list-item__lot-chevron {
  color: #8c6235 !important;
  font-size: .58rem;
}

.inventory-list-item__dates {
  display: grid;
  grid-template-columns: 1fr;
  gap: 5px;
}

.inventory-list-item__dates div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.inventory-list-item__dates strong {
  overflow: hidden;
  color: #455360;
  font-size: .71rem;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inventory-list-item__actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.inventory-list-item__actions .btn {
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 9px;
  font-size: .71rem;
  font-weight: 700;
  white-space: nowrap;
}

.inventory-list-item__actions .btn-light {
  color: #4e5d6d;
  background: #eef1f4;
}

@media (max-width: 1100px) {
  .inventory-list-item {
    grid-template-columns: 28px 70px minmax(0, 1fr) auto;
  }

  .inventory-list-item__selector,
  .inventory-list-item__media {
    grid-row: 1 / span 2;
  }

  .inventory-list-item__media {
    width: 70px;
  }

  .inventory-list-item__identity {
    grid-column: 3;
    grid-row: 1;
  }

  .inventory-list-item__account {
    grid-column: 3;
    grid-row: 2;
  }

  .inventory-list-item__storage {
    grid-column: 4;
    grid-row: 2;
    align-items: flex-end;
    flex-direction: row;
  }

  .inventory-list-item__dates {
    grid-column: 3 / 5;
    grid-row: 3;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding-top: 8px;
    border-top: 1px solid #edf0f3;
  }

  .inventory-list-item__actions {
    grid-column: 4;
    grid-row: 1;
  }
}

@media (max-width: 700px) {
  .inventory-list-item {
    grid-template-columns: 24px 62px minmax(0, 1fr);
    align-items: start;
    padding: 11px;
  }

  .inventory-list-item__selector {
    grid-column: 1;
    grid-row: 1 / span 2;
    padding-top: 19px;
  }

  .inventory-list-item__media {
    grid-column: 2;
    grid-row: 1;
    width: 62px;
    height: 52px;
  }

  .inventory-list-item__identity {
    grid-column: 3;
    grid-row: 1;
  }

  .inventory-list-item__account,
  .inventory-list-item__storage,
  .inventory-list-item__dates,
  .inventory-list-item__actions {
    grid-column: 2 / 4;
  }

  .inventory-list-item__account {
    grid-row: 2;
  }

  .inventory-list-item__storage {
    grid-row: 3;
    align-items: center;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .inventory-list-item__dates {
    grid-row: 4;
  }

  .inventory-list-item__actions {
    grid-row: 5;
    justify-content: stretch;
  }

  .inventory-list-item__actions .btn {
    flex: 1;
  }
}

@media (max-width: 430px) {
  .inventory-list-item__identity-topline {
    align-items: flex-start;
    flex-direction: column;
    gap: 3px;
  }

  .inventory-list-item__actions {
    flex-direction: column;
  }
}
</style>
